import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./PopperPopup.css";

export default function PopperPopup({ children, popupContent }) {
  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({
    position: "fixed",
    left: 0,
    top: 0,
    opacity: 0,
    pointerEvents: "none",
    zIndex: 9999,
  });

  const togglePopup = () => setOpen((prev) => !prev);

  const updatePosition = () => {
    const containerEl = containerRef.current;
    const popupEl = popupRef.current;
    if (!containerEl || !popupEl) return;

    // Temporarily show for measurement
    popupEl.style.visibility = "hidden";
    popupEl.style.display = "block";

    const popupRect = popupEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Align popup right edge with container's right edge
    let left = containerRect.right - popupRect.width;
    let top = containerRect.bottom + 8; // below container by default

    // Keep popup on screen
    left = Math.max(8, Math.min(left, screenWidth - popupRect.width - 8));
    if (top + popupRect.height > screenHeight - 8) {
      top = containerRect.top - popupRect.height - 8; // show above if no space below
    }
    if (top < 8) top = 8;

    setStyle({
      position: "fixed",
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: "300px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      pointerEvents: "auto",
      transition: "opacity 0.2s",
      zIndex: 9999,
    });

    popupEl.style.visibility = "";
  };

  useEffect(() => {
    if (open) updatePosition();
  }, [open]);

  // Close popup if click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        popupRef.current &&
        !popupRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="popper-container">
      <div onClick={togglePopup} style={{ display: "inline-block", cursor: "pointer" }}>
        {children}
      </div>

      {open &&
        popupContent &&
        createPortal(
          <div ref={popupRef} className="popper-text" style={style}>
            {React.isValidElement(popupContent)
              ? popupContent
              : typeof popupContent === "string"
              ? popupContent.split("\n").map((line, idx) => <div key={idx}>{line}</div>)
              : null}
          </div>,
          document.body
        )}
    </div>
  );
}
